// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { AccountWidgetAccountDetailsPanelController } from '@shared/commons/AccountWidget/AccountWidgetAccountDetailsPanelController';
import { AccountWidgetAccountsController } from '@shared/commons/AccountWidget/AccountWidgetAccountsController';
import { DataCache } from '@shared/commons/DataCache';
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { ApplicationLinkingColor } from '@shared/utils/Interfaces/IApplicationLinkingProvider.interface';
import { DockSystemInstance } from '../DockSystem';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { WorkSpaceManager } from '../WorkSpace/WorkSpaceManager';
class _LinkedSystem {
    constructor() {
        this.accountStorage = {};
        this.symbolStorage = {};
        this.inProgress = false;
        this.currentLinkOutColor = null;
        this._accLinkingActive = false;
        // #region IApplicationLinkingProvider
        this._instrumentsLinkingRecord = {
            [ApplicationLinkingColor.Yellow]: null,
            [ApplicationLinkingColor.Red]: null,
            [ApplicationLinkingColor.Green]: null,
            [ApplicationLinkingColor.Orange]: null,
            [ApplicationLinkingColor.Blue]: null,
            [ApplicationLinkingColor.Purple]: null
        };
        this._instrumentLinkingListeners = [];
        this.linkOutInstrument = (color, instrument) => {
            // Old subscribers will be notified about the change
            this.setSymbol(TerceraLinkControlConstants.getOldColorConstant(color), instrument.getInteriorId(), false);
            this._instrumentsLinkingRecord[color] = instrument;
            for (const listener of this._instrumentLinkingListeners) {
                listener(color, instrument);
            }
        };
        this.subscribeInstrumentLinking = (handler) => {
            this._instrumentLinkingListeners.push(handler);
        };
        this.unsubscribeInstrumentLinking = (handler) => {
            const index = this._instrumentLinkingListeners.indexOf(handler);
            this._instrumentLinkingListeners.splice(index, 1);
        };
        // #endregion IApplicationLinkingProvider
    }
    get accLinkingActive() {
        return this._accLinkingActive;
    }
    set accLinkingActive(value) {
        this._accLinkingActive = value;
        SessionSettings.save();
    }
    //
    // account id
    //
    setAccount(color, accountId) {
        const acc = DataCache.GetAccountById(accountId);
        if (!acc) {
            return;
        }
        if (acc.isMAM || this.accountStorage[color] === accountId) {
            return;
        }
        if (this.accLinkingActive && AccountWidgetAccountsController.Account !== acc) {
            AccountWidgetAccountsController.Account = acc; // AccountWidget is subscribed to changes of acc in the controller, so it calls the accLinkingOn method, which in turn calls setAccount again, but AccountWidgetAccountsController will already have the new acc.
            return;
        }
        // newSubscriber is obsoleted
        this.accountStorage[color] = accountId;
        this.accountLinkOut(color);
    }
    getAccount(color = LinkedSystemAccLinkingValue) {
        /* return value: AccountId */
        return this.accountStorage[color];
    }
    //
    // instrument FullName
    //
    setSymbol(color, symbolName, newSubscriber) {
        if ((newSubscriber && !this.symbolStorage[color]) ||
            (!newSubscriber && this.symbolStorage[color]) ||
            (!newSubscriber && !this.symbolStorage[color])) {
            this.symbolStorage[color] = symbolName;
        }
        this.symbolLinkOut(color);
    }
    getSymbol(color) {
        return this.symbolStorage[color];
    }
    accountLinkOut(color) {
        const value = this.getAccount(color);
        const valueControlTag = 'accountLinkValue';
        const method = 'accountLink_In';
        this.linkOut(value, color, valueControlTag, method);
    }
    symbolLinkOut(color) {
        const value = this.getSymbol(color);
        const valueControlTag = 'symbolLinkValue';
        const method = 'symbolLink_In';
        this.linkOut(value, color, valueControlTag, method);
    }
    updateDictState(isByAcc) {
        const valueControlTag = isByAcc ? 'accountLinkValue' : 'symbolLinkValue';
        let storage = isByAcc ? this.symbolStorage : this.accountStorage;
        const curWS = WorkSpaceManager.currentWorkspace;
        if (!curWS) {
            return;
        }
        const allPanels = curWS.allPanels;
        const colorConstants = TerceraLinkControlConstants.ColorConstants;
        const colorTabs = {};
        const keys = Object.keys(allPanels);
        const len = keys.length;
        for (let i = 0; i < len; i++) {
            const curPanel = allPanels[keys[i]];
            const myColor = curPanel.get(valueControlTag);
            if (myColor === colorConstants.NONE) {
                continue;
            }
            if (storage.hasOwnProperty(myColor)) {
                colorTabs[myColor] = storage[myColor];
            }
        }
        storage = colorTabs;
    }
    linkOut(value, color, valueControlTag, method) {
        try {
            const curWS = WorkSpaceManager.currentWorkspace;
            if (this.inProgress || !curWS) {
                return;
            }
            const allPanels = curWS.allPanels;
            const processedTabs = [];
            const colorConstants = TerceraLinkControlConstants.ColorConstants;
            const accountLinking = valueControlTag === 'accountLinkValue';
            if (accountLinking && this.accLinkingActive && AccountWidgetAccountDetailsPanelController.HasPanel()) {
                processedTabs.push(AccountWidgetAccountDetailsPanelController.AccountDetailsPanel);
            }
            let i = 0;
            const keys = Object.keys(allPanels);
            const keysLen = keys.length;
            for (i = 0; i < keysLen; i++) {
                const curPanel = allPanels[keys[i]];
                const myColor = curPanel.get(valueControlTag);
                if (myColor === colorConstants.NONE && !accountLinking) {
                    continue;
                }
                if (myColor === color ||
                    myColor === TerceraLinkControlConstants.STATE_ALL ||
                    accountLinking) // || myColor === colorConstants.ALL)
                 {
                    processedTabs.push(curPanel);
                }
            }
            this.inProgress = true;
            this.currentLinkOutColor = color;
            const processedTabsLen = processedTabs.length;
            for (i = 0; i < processedTabsLen; i++) {
                const curPanel = processedTabs[i];
                curPanel[method](value, true);
            }
            this.inProgress = false;
            this.currentLinkOutColor = null;
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            this.inProgress = false;
            this.currentLinkOutColor = null;
        }
    }
    accLinkingOn(accId) {
        const value = LinkedSystemAccLinkingValue;
        this.setAccount(value, accId);
        const allPanels = WorkSpaceManager.currentWorkspace.allPanels;
        for (const id in allPanels) {
            allPanels[id].updateAccountLinkVisible(true);
            allPanels[id].accountLink_In(accId, true);
        }
        DockSystemInstance.resizeDockSystem();
    }
    accLinkingOff() {
        if (isNullOrUndefined(WorkSpaceManager.currentWorkspace)) {
            return;
        }
        const allPanels = WorkSpaceManager.currentWorkspace.allPanels;
        for (const id in allPanels) {
            allPanels[id].disableAccountLinking();
        }
        DockSystemInstance.resizeDockSystem();
    }
    Clear() {
        this.accountStorage = {};
        this.symbolStorage = {};
        this.inProgress = false;
        this.accLinkingActive = false;
        this.accLinkingOff();
    }
}
export const LinkedSystemAccLinkingValue = -2;
export const LinkedSystem = new _LinkedSystem();
